'use client'

import type { MutationFunction } from '@apollo/client'
import { Button, Input, PlusIcon, Popover, PopoverContent, PopoverTrigger } from '@chain-zen/ui'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

interface AddTrackingInputProps {
  buttonText: string
  errorMessage: string
  inputName: string
  mutation: MutationFunction
  placeholder: string
  shipmentID: string
  successMessage: string
  validationMessage: string
  validationPattern: RegExp
}

/**
 * This component is used to add tracking information to a shipment.
 */
export const AddTrackingInput = ({
  shipmentID,
  inputName,
  buttonText,
  placeholder,
  mutation,
  validationPattern,
  validationMessage,
  successMessage,
  errorMessage
}: AddTrackingInputProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      [inputName]: ''
    }
  })

  const onSubmit = async (data: Record<string, string>) => {
    setIsLoading(true)
    setError(null)

    try {
      await mutation({
        variables: {
          [inputName]: data[inputName],
          shipmentID
        }
      })

      toast.success(successMessage)
      setIsOpen(false)
      reset()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : errorMessage
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
      <PopoverTrigger asChild={true}>
        <div className="flex items-center gap-2 hover:underline cursor-pointer text-sm">
          <PlusIcon className="h-4 w-4" />
          {buttonText}
        </div>
      </PopoverTrigger>

      <PopoverContent className="p-2 md:w-136 w-72" align="start" side="bottom">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Input
              {...register(inputName, {
                pattern: {
                  message: validationMessage,
                  value: validationPattern
                },
                required: `${inputName} is required`
              })}
              placeholder={placeholder}
              className="w-full"
              disabled={isLoading}
            />

            {!!errors[inputName] && <p className="text-sm text-destructive">{errors[inputName]?.message as string}</p>}
          </div>

          {!!error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false)
                reset()
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
